<template>
  <div class="login-page">

    <!-- ── Left Panel: Slideshow ───────────────────────────────── -->
    <div class="login-left d-none d-lg-flex">

      <!-- Stacked slides (all rendered, z-index controls visibility) -->
      <div
        v-for="(slide, i) in slides"
        :key="i"
        class="slide-bg"
        :class="{ active: i === currentSlide, prev: i === prevSlide }"
        :style="{ backgroundImage: `url(${slide.image})` }"
      ></div>

      <!-- Bottom vignette -->
      <div class="slide-vignette"></div>
      <!-- Dark overlay -->
      <div class="slide-overlay"></div>

      <!-- Content on top of image -->
      <div class="slide-content">

        <!-- Logo top-left -->
        <div class="slide-logo-wrap">
          <div class="slide-logo-bg">
            <img
              :src="title.image_url"
              class="slide-logo"
              onerror="this.onerror=null; this.src='/assets/img/kgshia_logo.png'"
            />
          </div>
          <span class="slide-logo-name">{{ title.agency_name }}</span>
        </div>

        <!-- Spacer -->
        <div class="flex-grow-1"></div>

        <!-- Slide text -->
        <transition name="text-up" mode="out-in">
          <div class="slide-text-block" :key="currentSlide">
            <div class="slide-tag">Universal Health Coverage</div>
            <h2 class="slide-title">{{ slides[currentSlide].title }}</h2>
            <p class="slide-desc">{{ slides[currentSlide].desc }}</p>
          </div>
        </transition>

        <!-- Progress bar + dots -->
        <div class="slide-footer">
          <div class="slide-progress-wrap">
            <div class="slide-progress-track">
              <div class="slide-progress-fill" :key="currentSlide"></div>
            </div>
          </div>
          <div class="slide-dots">
            <span
              v-for="(s, i) in slides"
              :key="i"
              :class="['dot', { active: i === currentSlide }]"
              @click="goToSlide(i)"
            ></span>
          </div>
        </div>

      </div>
    </div>

    <!-- ── Right Panel: Login Form ─────────────────────────────── -->
    <div class="login-right">

      <!-- Logo pinned top-right -->
      <div class="right-logo">
        <img
          :src="title.image_url"
          onerror="this.onerror=null; this.src='/assets/img/kgshia_logo.png'"
        />
      </div>

      <div class="login-form-wrap">

        <!-- Heading -->
        <div class="mb-5">
          <h3 class="login-heading mb-1">Welcome back</h3>
          <p class="text-muted" style="font-size: .9rem;">Sign in to your account to continue</p>
        </div>

        <!-- Email -->
        <div class="form-group mb-4">
          <label class="small font-weight-bold text-secondary mb-1">Email or Username</label>
          <div class="input-group input-group-lg">
            <div class="input-group-prepend">
              <span class="input-group-text field-icon">
                <i class="fe fe-mail"></i>
              </span>
            </div>
            <input
              type="text"
              required
              class="form-control field-input"
              placeholder="Enter your email or username"
              v-model="identifier"
              @keyup.enter="logIn"
            />
          </div>
        </div>

        <!-- Password -->
        <div class="form-group mb-4">
          <label class="small font-weight-bold text-secondary mb-1">Password</label>
          <div class="input-group input-group-lg">
            <div class="input-group-prepend">
              <span class="input-group-text field-icon">
                <i class="fe fe-lock"></i>
              </span>
            </div>
            <input
              :type="showPassword ? 'text' : 'password'"
              required
              class="form-control field-input field-input-mid"
              placeholder="Enter your password"
              v-model="password"
              @keyup.enter="logIn"
            />
            <div class="input-group-append">
              <span class="input-group-text field-icon field-eye" @click="showPassword = !showPassword">
                <i :class="`fe fe-${showPassword ? 'eye-off' : 'eye'}`"></i>
              </span>
            </div>
          </div>
        </div>

        <!-- Remember / Forgot Password -->
        <div class="d-flex align-items-center justify-content-between mb-5">
          <div class="form-check mb-0">
            <input class="form-check-input" type="checkbox" id="rememberMe" />
            <label class="form-check-label small text-muted" for="rememberMe">Remember me</label>
          </div>
          <a href="/offline-home" class="small" style="color: #2563eb;" v-if="user != null">Offline Access</a>
        </div>

        <!-- Sign in button -->
        <button
          type="button"
          class="btn btn-block btn-signin font-weight-bold"
          @click="logIn"
          :disabled="isLoading"
        >
          <span v-if="isLoading">
            <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
            Signing in…
          </span>
          <span v-else>
            <i class="fe fe-log-in mr-2"></i> Sign In
          </span>
        </button>

        <p class="text-center small text-muted mt-3 mb-0">
          By signing in you agree to the
          <router-link to="/terms" style="color: #2563eb;">Terms of Use</router-link> and
          <router-link to="/privacy" style="color: #2563eb;">Privacy Policy</router-link>.
          <router-link to="/cookies" style="color: #2563eb;">Cookie Policy</router-link>
        </p>

        <p class="text-center text-muted mt-5 mb-0" style="font-size: .75rem;">
          &copy; {{ new Date().getFullYear() }} {{ title.agency_name }}. All rights reserved.
        </p>

      </div>
    </div>

    <div class="vld-parent">
      <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
    </div>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

let url = window.location.href;

export default {
  components: { Loading },
  data() {
    return {
      identifier: "",
      password: "",
      showPassword: false,
      password_confirmation: "",
      response: "",
      isLoading: false,
      fullPage: true,
      auth_user: "",
      user: null,
      currentSlide: 0,
      prevSlide: null,
      slideTimer: null,
      title: {
        id: null,
        agency_name: "Kogi State Health Insurance Agency",
        image_url: "/assets/img/kgshia_logo.png",
        favicon: "/assets/img/kgshia_logo.png",
        primary_color: "#17a2b8",
        secondary_color: "#6c757d",
        tertiary_color: "#28a745",
      },
      slideImages: [
        "/background/1.jpg",
        "/background/2.jpg",
        "/background/3.jpg",
      ],
      reg: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/,
    };
  },
  computed: {
    slides() {
      const texts = [
        { title: "Quality Healthcare for All",      desc: "Connecting enrollees with trusted healthcare providers across the state." },
        { title: "Empowering Communities",           desc: "Building healthier communities through comprehensive health insurance coverage." },
        { title: "Seamless Claims Processing",       desc: "Fast, transparent, and reliable health insurance claims management." },
      ];
      return this.slideImages.map((img, i) => ({
        image: img,
        ...texts[i % texts.length],
      }));
    },
  },
  mounted() {
    this.axios
      .post(`/api/v1/auth/hmo/url`, { url })
      .then((response) => {
        this.title = response.data.data;
        document.title = this.title.agency_name + " Portal";
        let favicon = document.getElementById("favicon");
        if (favicon) favicon.href = this.title.image_url;
      })
      .catch((error) => {
        console.error(error);
      });

    this.startTimer();
  },
  beforeDestroy() {
    clearInterval(this.slideTimer);
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
  },
  methods: {
    startTimer() {
      this.slideTimer = setInterval(() => {
        this.prevSlide = this.currentSlide;
        this.currentSlide = (this.currentSlide + 1) % this.slides.length;
      }, 5500);
    },
    goToSlide(i) {
      this.prevSlide = this.currentSlide;
      this.currentSlide = i;
      clearInterval(this.slideTimer);
      this.startTimer();
    },
    logIn() {
      this.isLoading = true;
      if (this.reg.test(this.identifier)) {
        this.axios
          .post("/api/v1/auth/login", {
            email: this.identifier,
            password: this.password,
            password_confirmation: this.password,
          })
          .then((response) => {
            let token = response.data.token;
            let user = response.data.user;
            let agency = response.data.agency;
            if (token) {
              localStorage.setItem("jwt", token);
              localStorage.setItem("user", JSON.stringify(user));
              if (agency) localStorage.setItem("agency", JSON.stringify(agency));
              if (localStorage.getItem("jwt") != null) {
                this.$router.push("/pusher");
              }
            }
          })
          .catch((error) => {
            console.log(error.response);
            this.response = error.response.data.error;
            this.isLoading = false;
            this.$toasted.error(`${this.response}`, {
              position: "top-center",
              duration: 3000,
            });
          });
      } else {
        this.axios
          .post("/api/v1/auth/login", {
            username: this.identifier,
            password: this.password,
          })
          .then((response) => {
            let token = response.data.token;
            if (token) {
              localStorage.setItem("jwt", token);
              if (localStorage.getItem("jwt") != null) {
                this.$router.push("/pusher");
              }
            }
          })
          .catch((error) => {
            console.log(error.response);
            this.isLoading = false;
            this.$toasted.error("Username or Password Incorrect", {
              position: "top-center",
              duration: 3000,
            });
          });
      }
    },
  },
};
</script>

<style scoped>
/* ── Page ────────────────────────────────────────────────── */
.login-page {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: #fff;
}

/* ── Left Panel ──────────────────────────────────────────── */
.login-left {
  position: relative;
  flex: 0 0 58%;
  overflow: hidden;
  flex-direction: column;
}

/* All slides stacked; CSS controls cross-fade + Ken Burns */
.slide-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  opacity: 0;
  transform: scale(1);
  filter: saturate(1.2) contrast(1.06);
  transition: opacity 1.4s ease;
  will-change: opacity, transform;
}

.slide-bg.active {
  opacity: 1;
  animation: ken-burns 6s ease-out forwards;
}

.slide-bg.prev {
  opacity: 0;
  transition: opacity 1.4s ease;
}

@keyframes ken-burns {
  0%   { transform: scale(1);    }
  100% { transform: scale(1.09); }
}

/* Vignette from bottom */
.slide-vignette {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.35) 40%, transparent 65%);
  z-index: 1;
}

/* Blue-toned overlay */
.slide-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    155deg,
    rgba(10, 22, 50, 0.45) 0%,
    rgba(30, 58, 95, 0.25) 60%,
    rgba(37, 99, 235, 0.12) 100%
  );
  z-index: 1;
}

.slide-content {
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 44px 52px 40px;
  color: #fff;
}

/* Logo top-left of left panel */
.slide-logo-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.slide-logo-bg {
  background: #fff;
  border-radius: 16px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(6px);
  box-shadow: 0 8px 28px rgba(0,0,0,0.35);
  flex-shrink: 0;
}

.slide-logo {
  height: 76px;
  width: auto;
  display: block;
}

.slide-logo-name {
  font-size: 1.2rem;
  font-weight: 800;
  opacity: 1;
  letter-spacing: .02em;
  text-shadow: 0 2px 10px rgba(0,0,0,0.55);
}

/* Text block */
.slide-text-block {
  margin-bottom: 32px;
}

.slide-tag {
  display: inline-block;
  font-size: .78rem;
  font-weight: 800;
  letter-spacing: .12em;
  text-transform: uppercase;
  background: linear-gradient(135deg, #2563eb, #7c3aed);
  border: 1px solid rgba(255,255,255,0.35);
  box-shadow: 0 4px 14px rgba(37,99,235,0.45);
  border-radius: 20px;
  padding: 3px 12px;
  margin-bottom: 16px;
  backdrop-filter: blur(4px);
}

.slide-title {
  font-size: 2.8rem;
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -0.025em;
  margin-bottom: 16px;
  text-shadow: 0 3px 18px rgba(0,0,0,0.5);
}

.slide-desc {
  font-size: 1.12rem;
  font-weight: 500;
  opacity: .95;
  text-shadow: 0 1px 8px rgba(0,0,0,0.5);
  line-height: 1.7;
  max-width: 420px;
}

/* Footer: progress bar + dots */
.slide-footer {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.slide-progress-wrap {
  width: 100%;
}

.slide-progress-track {
  height: 4px;
  background: rgba(255,255,255,0.2);
  border-radius: 2px;
  overflow: hidden;
  width: 100%;
}

.slide-progress-fill {
  height: 100%;
  width: 0%;
  background: rgba(255,255,255,0.85);
  border-radius: 2px;
  animation: fill-progress 5.5s linear forwards;
}

@keyframes fill-progress {
  from { width: 0%; }
  to   { width: 100%; }
}

.slide-dots {
  display: flex;
  gap: 8px;
  align-items: center;
}

.dot {
  height: 8px;
  width: 8px;
  border-radius: 4px;
  background: rgba(255,255,255,0.5);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(.4,0,.2,1);
}

.dot.active {
  width: 34px;
  background: #fff;
}

/* ── Right Panel ─────────────────────────────────────────── */
.login-right {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 40px;
  background: #fff;
  overflow-y: auto;
}

/* Small logo pinned to top-right */
.right-logo {
  position: absolute;
  top: 24px;
  right: 28px;
}

.right-logo img {
  height: 64px;
  opacity: 1;
  width: auto;
}

.login-form-wrap {
  width: 100%;
  max-width: 380px;
}

.login-heading {
  font-size: 2rem;
  font-weight: 900;
  letter-spacing: -0.025em;
  color: #0f172a;
}

/* ── Form inputs ─────────────────────────────────────────── */
.field-icon {
  background: #f7f8fc;
  border-right: 0;
  border-color: #e5e7ef;
  color: #94a3b8;
}

.field-input {
  border-left: 0;
  border-color: #e5e7ef;
  background: #f7f8fc;
}

.field-input-mid {
  border-right: 0;
}

.field-eye {
  border-left: 0 !important;
  cursor: pointer;
}

.form-control:focus {
  background: #fff;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
}

.input-group:focus-within .field-icon {
  border-color: #2563eb;
  background: #fff;
}

/* Sign-in button */
.btn-signin {
  background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 50%, #7c3aed 100%);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 15px;
  font-size: 1.05rem;
  font-weight: 800 !important;
  letter-spacing: .03em;
  box-shadow: 0 8px 24px rgba(37,99,235,0.45);
  transition: transform 0.15s, box-shadow 0.15s;
}

.btn-signin:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 30px rgba(37,99,235,0.55);
  color: #fff;
}

.btn-signin:active:not(:disabled) {
  transform: translateY(0);
}

/* ── Text transitions ────────────────────────────────────── */
.text-up-enter-active {
  transition: opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s;
}
.text-up-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.text-up-enter {
  opacity: 0;
  transform: translateY(22px);
}
.text-up-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
</style>
